import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  getIsWorkflowChartMeasure,
  getIsWorkflowChartType,
  getIsWorkflowListGroupableDimension,
  getIsWorkflowSystemChartPresetId,
  getResolvedWorkflowChartGrain,
  getWorkflowCycleTimeDays,
  getWorkflowSystemChartPreset,
  getWorkflowViewStateFromSystemChartPreset,
  MAX_WORKFLOW_CHART_DIMENSIONS,
  WORKFLOW_CHART_DIMENSIONS,
  WORKFLOW_CHART_GRAINS,
  WORKFLOW_CHART_MEASURE_LABELS,
  WORKFLOW_CHART_MEASURES,
  WORKFLOW_CHART_TYPES,
} from '@aline/common';
import type { WorkflowChartMeasure, WorkflowChartType, WorkflowViewState } from '@aline/common';
import { link, params } from '@aline/routes';
import {
  Alert,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { configurationOptions } from '@mui/x-charts-premium/ChartsRenderer';
import materialGridSlots from '@mui/x-data-grid/material';
import type {
  GridApiPremium,
  GridChartsPanelProps,
  GridGroupNode,
  GridRowGroupingModel,
  GridSlotProps,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import {
  DataGridPremium,
  GridChartsIntegrationContextProvider,
  GridChartsPanel,
  GridChartsRendererProxy,
  gridClasses,
  gridColumnVisibilityModelSelector,
  GridLogicOperator,
  GridPreferencePanelsValue,
  gridSidebarOpenSelector,
  useGridApiContext,
  useGridApiRef,
  useGridChartsIntegrationContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { capitalCase } from 'change-case';
import { isEqual } from 'lodash';
import { ChevronDown, ListFilter, Plus, SlidersHorizontal, X } from 'lucide-react';
import {
  allBoardFilters,
  allColumnCategoryFilters,
  allColumnFilters,
  allLabelFilters,
  allOwnerFilters,
  allWorkflowAssigneeFilters,
  allWorkflowPriorityFilters,
  allWorkflowStateFilters,
  getStringOperatorsWithSafeIsAnyOf,
  ListCountFooter,
  ListEmpty,
  OwnerCell,
  ReportFiltersPanel,
  reportRowHeightPx,
} from 'src/components';
import type { ListCountFooterProps } from 'src/components';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { GridAlineTooltip } from 'src/components/generic/data-grid/grid-aline-tooltip';
import { WorkflowIcon } from 'src/components/icons/WorkflowIcon';
import { InsightChartCard } from 'src/components/insights/InsightChartCard';
import {
  composeFilterModelWithDrilldown,
  omitDrilldownFilterItems,
  parseWorkflowDrilldownPayload,
} from 'src/components/insights/workflow-drilldown';
import type { WorkflowDrilldownBucket } from 'src/components/insights/workflow-drilldown';
import { dateValueGetter } from 'src/components/reports/helpers';
import { Apollo } from 'src/gql/generated';
import { useSelectCurrentUser } from 'src/gql/hooks/user';
import { getIsBrowserHandledLinkClick, openLink, useConsumePendingParam } from 'src/helpers';
import type { WorkflowFileSubset } from 'src/hooks';
import { createDataGridPremiumPaperStyles, useWorkflowDataSource } from 'src/hooks';
import type { LabelSubset, UserWithOrg } from 'src/types';
import type { WorkflowPermissionSubset } from 'src/types/apollo/workflow-permission';
import { makeStyles } from 'tss-react/mui';

import {
  getWorkflowListGroupAwareRenderCell,
  WorkflowListBoardCell,
  WorkflowListColumnCell,
  WorkflowListDateCell,
  WorkflowListLabelCell,
  WorkflowListTitleCell,
  WorkflowListWorkflowTypeCell,
} from './cells';
import { WorkflowAssigneesCell } from './cells/WorkflowAssignees';
import { workflowDateFilterOperators } from './relative-date-filter-operators';
import { WorkflowListChartFieldsPrompt } from './workflow-list-chart-preview';
import {
  addWorkflowListChart,
  applyChartsIntegrationToGrid,
  createDefaultWorkflowListChartConfig,
  getAppliedWorkflowListStateFromWorkflowView,
  getIsWorkflowListChartableField,
  getIsWorkflowListChartsSectionMenuItemAllowed,
  getShouldSetWorkflowListNameColumnVisibility,
  getWorkflowListChartDateBucket,
  getWorkflowListChartDimensionItems,
  getWorkflowListChartDimensions,
  getWorkflowListChartIdsForSync,
  getWorkflowListChartLabel,
  getWorkflowListChartMeasure,
  getWorkflowListChartProxySyncAction,
  getWorkflowListChartType,
  getWorkflowListGroupFieldsFromChartDimensions,
  getWorkflowListSeriesInput,
  getWorkflowListSingleChartValue,
  getWorkflowViewStateFromWorkflowList,
  pushChartsIntegrationIfChanged,
  UNTITLED_WORKFLOW_LIST_CHART_TITLE,
  WORKFLOW_LIST_CHART_ID,
  WORKFLOW_LIST_CHART_PROXY_SYNC,
  WORKFLOW_LIST_CHARTS_DIMENSIONS_LABEL,
  WORKFLOW_LIST_CHARTS_VALUES_LABEL,
} from './workflow-list-charts';
import type {
  WorkflowListChartConfig,
  WorkflowListChartsIntegrationSlice,
} from './workflow-list-charts';
import {
  getIsWorkflowListGroupRow,
  getIsWorkflowListWorkflowRow,
  getWorkflowIdFromListRow,
} from './workflow-list-row-guards';
import { allWorkflowTypeFilters } from './workflow-type-filters';
import type { WorkflowListPresetId, WorkflowListPresetUser } from './workflow-views';
import { getIsGridFilterModel, getWorkflowListPresetFilterModel } from './workflow-views';

export { getIsWorkflowListGroupRow, getIsWorkflowListWorkflowRow, getWorkflowIdFromListRow };

/** Shared ID for hover-visible buttons in workflow/template lists */
export const workflowListButtonId = 'workflow-quick-view';

/**
 * Floor for the list once the Chart preview shares the route with it — the two
 * together are taller than most viewports, so the route scrolls rather than
 * squeezing the list down to a couple of rows.
 */
const WORKFLOW_LIST_WITH_PREVIEW_MIN_HEIGHT_PX = 840;

/**
 * How long the Charts panel stays outlined after a Chart asks to be configured.
 * Long enough to survive the scroll and be noticed, short enough that it reads as
 * a pointer rather than a permanent state.
 */
const WORKFLOW_LIST_CHARTS_PANEL_SPOTLIGHT_MS = 7500;

/**
 * The Charts panel body — the tab pane under the tab strip. MUI styles that pane
 * with an unnamed emotion class, so the ARIA role is the only stable handle.
 */
const WORKFLOW_LIST_CHARTS_PANEL_BODY_SELECTOR = '[role="tabpanel"]';

const useStyles = makeStyles<{ chartPreviewOpen: boolean }>()((theme, { chartPreviewOpen }) => ({
  chartPreview: {
    '@media (max-width: 900px)': {
      /**
       * A single column leaves a wide Chart's `span 2` with nowhere to go, and
       * the grid would add an implicit second column to fit it.
       */
      '&& > *': {
        gridColumn: 'span 1',
      },
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
    display: 'grid',
    flexShrink: 0,
    gap: theme.spacing(1),
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    marginBottom: theme.spacing(1.5),
  },
  /**
   * Rings the panel's active tab body after a Chart card sends the user to it.
   * The ring is an outline rather than a border or inset shadow: outlines paint
   * above descendants, so the panel's own rows and toolbars cannot break it up,
   * and a negative offset keeps it inside the body without shifting layout.
   */
  chartsPanelSpotlight: {
    [`&& .${gridClasses.sidebar} ${WORKFLOW_LIST_CHARTS_PANEL_BODY_SELECTOR}`]: {
      borderRadius: 4,
      outline: `2px solid ${theme.appColors.primary}`,
      outlineOffset: -2,
    },
  },
  drilldownBanner: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  hoverEffectRow: {
    backgroundColor: theme.appColors.selectedBackground,
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    minWidth: 0,
    overflowY: chartPreviewOpen ? 'auto' : 'hidden',
  },
  toolbarHeading: {
    alignItems: 'baseline',
    display: 'flex',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  workflowsToolbar: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderBottom: 0,
    borderRadius: '12px 12px 0 0',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'space-between',
    minHeight: 48,
    padding: theme.spacing(0.5, 1.5),
  },
  xGrid: {
    /**
     * The server-series contract selects a Measure by field, so MUI's per-field
     * aggregation selector is redundant and offers combinations we cannot render.
     */
    '& .MuiDataGrid-chartsPanelDataFieldActionContainer > .MuiChip-root': {
      display: 'none',
    },
    /**
     * The connected Workflows toolbar owns the rounded top edge, so everything the
     * paper `sx` rounds along the grid's top has to square off beneath it. Doubling
     * the class outranks that `sx`, which is inserted after this stylesheet.
     */
    ...(chartPreviewOpen
      ? {
          [`&& .${gridClasses.columnHeaders}, && .${gridClasses.main}`]: {
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          },
          [`&& .${gridClasses.sidebar}`]: {
            borderTopRightRadius: 0,
          },
        }
      : {}),
    flexGrow: 1,
    minHeight: chartPreviewOpen ? WORKFLOW_LIST_WITH_PREVIEW_MIN_HEIGHT_PX : 0,
    minWidth: 0,
    [`& .${gridClasses.row}`]: {
      [`&:hover #${workflowListButtonId}`]: {
        visibility: 'visible',
      },
    },
  },
}));

const useChartsPanelStyles = makeStyles()(theme => ({
  chartsPanelHeader: {
    /**
     * `GridChartsPanel` has no header slot and renders its own header immediately
     * after this one; hide it so the panel keeps a single header row.
     */
    '& + div': {
      display: 'none',
    },
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 0.5, 0, 0.75),
  },
  chartsPanelSelector: {
    marginRight: 'auto',
    textTransform: 'none',
  },
}));

export interface WorkflowListRow extends Pick<
  Apollo.Workflow,
  | 'completedAt'
  | 'createdAt'
  | 'id'
  | 'name'
  | 'priority'
  | 'startedAt'
  | 'status'
  | 'state'
  | 'variant'
  | 'updatedAt'
  | '__typename'
  | 'column'
  | 'doneColumn'
> {
  files: WorkflowFileSubset[];
  labels: LabelSubset[];
  linkedOpportunities: Pick<
    Apollo.LinkedOpportunity,
    'id' | 'integration' | 'opportunityName' | '__typename'
  >[];
  owner: UserWithOrg;
  parties: Pick<Apollo.Party, 'id' | 'name' | '__typename'>[];
  permissions: WorkflowPermissionSubset[];
  phases: Array<
    Pick<Apollo.Phase, 'completedAt' | 'id' | 'name' | 'order' | 'startedAt'> & {
      column: Pick<Apollo.Column, 'id'>;
      __typename?: string;
    }
  >;
  sourceWorkflow: Pick<Apollo.Workflow, 'id' | 'name' | 'variant' | '__typename'> | null;
  isGroupRow?: undefined;
  /** Present on grouped leaf rows (composite DataGrid id). */
  workflowId?: string;
}

export type WorkflowListGroupRow = {
  groupField: string;
  groupKey: string;
  groupLabel: string;
  id: string;
  isGroupRow: true;
  name: string;
  workflowCount: number;
  __typename?: undefined;
  avgCycleTime?: number | null;
  childRowCount?: number | null;
  count?: number;
  medianDurationDays?: number | null;
  parentGroupId?: string | null;
};

/**
 * Rows the grid builds for itself, such as lazy-loading placeholders. They carry
 * none of the Workflow or group fields, so a cell has to narrow before reading
 * anything off a row.
 */
export type WorkflowListGeneratedRow = {
  __typename?: undefined;
  id?: string;
  isGroupRow?: undefined;
};

export type WorkflowListGridRow = WorkflowListRow | WorkflowListGroupRow | WorkflowListGeneratedRow;

function getGroupingColumnLabel(row: WorkflowListGridRow): string {
  if (getIsWorkflowListGroupRow(row)) return row.groupLabel;
  if (getIsWorkflowListWorkflowRow(row)) return row.name ?? '';
  return '';
}

/**
 * Chart-type schema for the built-in Charts panel. Every type listed here has to be
 * renderable by the server-series InsightChartCard (ADR 0004), which is what the user
 * actually sees — the panel's own renderer is bypassed.
 */
const WORKFLOW_LIST_CHARTS_PANEL_SCHEMA = {
  [WORKFLOW_CHART_TYPES.area]: {
    ...configurationOptions.area,
    dimensionsLabel: WORKFLOW_LIST_CHARTS_DIMENSIONS_LABEL,
    valuesLabel: WORKFLOW_LIST_CHARTS_VALUES_LABEL,
  },
  [WORKFLOW_CHART_TYPES.bar]: {
    ...configurationOptions.bar,
    dimensionsLabel: WORKFLOW_LIST_CHARTS_DIMENSIONS_LABEL,
    valuesLabel: WORKFLOW_LIST_CHARTS_VALUES_LABEL,
  },
  [WORKFLOW_CHART_TYPES.column]: {
    ...configurationOptions.column,
    dimensionsLabel: WORKFLOW_LIST_CHARTS_DIMENSIONS_LABEL,
    valuesLabel: WORKFLOW_LIST_CHARTS_VALUES_LABEL,
  },
  [WORKFLOW_CHART_TYPES.line]: {
    ...configurationOptions.line,
    dimensionsLabel: WORKFLOW_LIST_CHARTS_DIMENSIONS_LABEL,
    valuesLabel: WORKFLOW_LIST_CHARTS_VALUES_LABEL,
  },
  [WORKFLOW_CHART_TYPES.pie]: {
    ...configurationOptions.pie,
    dimensionsLabel: WORKFLOW_LIST_CHARTS_DIMENSIONS_LABEL,
    valuesLabel: WORKFLOW_LIST_CHARTS_VALUES_LABEL,
  },
};

/**
 * `GridChartsPanel` hardcodes a Customize tab that edits chart-renderer props. The
 * visible chart is the server-series InsightChartCard (ADR 0004), which ignores them,
 * so the tab is dropped instead of shown with nothing behind it.
 */
const WORKFLOW_LIST_CHARTS_UNSUPPORTED_TAB = 'customize';

/**
 * `GridChartsPanel` owns its tab state internally, so the tab is controlled from here:
 * a Chart card's entry points decide whether the user lands on the type picker or on
 * the field pickers.
 */
const WORKFLOW_LIST_CHARTS_TABS = {
  chart: 'chart',
  fields: 'data',
} as const;

type WorkflowListChartsTab =
  (typeof WORKFLOW_LIST_CHARTS_TABS)[keyof typeof WORKFLOW_LIST_CHARTS_TABS];

type WorkflowListBuilderPanelContextValue = {
  chartTitles: Record<string, string>;
  onAddChart: () => void;
  readOnly: boolean;
};

const WorkflowListBuilderPanelContext =
  React.createContext<WorkflowListBuilderPanelContextValue | null>(null);

/** Server-side aggregation function stubs (labels only; values come from dataSource). */
const WORKFLOW_LIST_AGGREGATION_FUNCTIONS = {
  [WORKFLOW_CHART_MEASURES.medianDurationDays]: {
    columnTypes: ['number'],
    label: WORKFLOW_CHART_MEASURE_LABELS[WORKFLOW_CHART_MEASURES.medianDurationDays],
  },
  [WORKFLOW_CHART_MEASURES.count]: {
    label: WORKFLOW_CHART_MEASURE_LABELS[WORKFLOW_CHART_MEASURES.count],
  },
};

/**
 * Column and Charts-panel headings for the hidden Measure columns. Shorter than
 * the shared Measure labels, which carry units for the aggregation footer.
 */
const WORKFLOW_LIST_MEASURE_COLUMN_NAMES = {
  [WORKFLOW_CHART_MEASURES.medianDurationDays]: 'Typical time (days)',
  [WORKFLOW_CHART_MEASURES.count]: 'Count',
} as const satisfies Record<WorkflowChartMeasure, string>;

/**
 * The aggregation model exists to mirror the active Chart's Measure onto the
 * hidden measure columns, not because a View asked the list for totals. MUI's
 * default position puts a footer row under the last row for it, which lands as
 * an empty sticky row, so only group rows aggregate.
 */
function getWorkflowListAggregationPosition(groupNode: GridGroupNode) {
  return groupNode.depth === -1 ? null : 'inline';
}

interface WorkflowsListProps {
  chartsIntegrationSlice: WorkflowListChartsIntegrationSlice;
  filtersAnchorEl: HTMLElement | null;
  onApiRefChange: (apiRef: React.RefObject<GridApiPremium | null> | null) => void;
  onChartsIntegrationSliceChange: (slice: WorkflowListChartsIntegrationSlice) => void;
  workflowListPreset: WorkflowListPresetId;
  activeWorkflowViewId?: string | null;
  chartFieldsRequestKey?: number;
  chartTitleEditingKey?: string | null;
  initialViewState?: unknown;
  onActiveChartTitleChange?: (chartId: string, title: string) => void;
  onAddChart?: () => void;
  onViewStateChange?: (state: ReturnType<typeof getWorkflowViewStateFromWorkflowList>) => void;
  readOnly?: boolean;
  /** Owner-supplied actions for a Chart card's header, rendered on every card. */
  renderChartActions?: (chartId: string) => React.ReactNode;
  /** Owner-supplied header content rendered ahead of the card's built-in controls. */
  renderChartLeadingActions?: (chartId: string) => React.ReactNode;
  variant?: 'chart-builder' | 'list';
}

export function WorkflowsList({
  activeWorkflowViewId = null,
  chartFieldsRequestKey = 0,
  chartTitleEditingKey,
  chartsIntegrationSlice,
  filtersAnchorEl,
  initialViewState,
  onActiveChartTitleChange,
  onAddChart,
  onApiRefChange,
  onChartsIntegrationSliceChange,
  onViewStateChange,
  readOnly = false,
  renderChartActions,
  renderChartLeadingActions,
  variant = 'list',
  workflowListPreset,
}: WorkflowsListProps) {
  const apiRef = useGridApiRef();
  const currentUser = useSelectCurrentUser();
  const appliedViewKeyRef = useRef<string | null>(null);
  const appliedInitialState = useMemo(
    () => (initialViewState ? getAppliedWorkflowListStateFromWorkflowView(initialViewState) : null),
    [initialViewState]
  );
  const workflowFilter = useMemo(
    () => ({
      completed: appliedInitialState?.workflowFilter?.completed,
      published: appliedInitialState?.workflowFilter?.published,
      variant:
        appliedInitialState?.workflowFilter?.variant === Apollo.WorkflowVariant.Template
          ? Apollo.WorkflowVariant.Template
          : Apollo.WorkflowVariant.Contract,
    }),
    [appliedInitialState?.workflowFilter]
  );
  /**
   * Seeded from the View so a saved grouping is in place for the grid's first
   * fetch; grouping in later has to throw away the flat rows it already loaded.
   */
  const [rowGroupingModel, setRowGroupingModel] = useState<GridRowGroupingModel>(
    () => appliedInitialState?.rowGroupingModel ?? []
  );
  const [chartsPanelTab, setChartsPanelTab] = useState<string>(WORKFLOW_LIST_CHARTS_TABS.chart);
  const { classes, cx } = useStyles({
    chartPreviewOpen: variant === 'chart-builder',
  });
  const handledChartFieldsRequestRef = useRef(0);
  /**
   * The grid is seeded with a default Chart and offers no API to remove one, so a
   * View whose Chart is keyed anything else would otherwise render that seeded
   * Chart alongside its own — and report it as an unsaved change forever.
   */
  const gridOnlyChartIdsRef = useRef<string[]>([]);
  const appliedBuilderStateRef = useRef<unknown>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const chartsIntegrationSliceRef = useRef(chartsIntegrationSlice);
  chartsIntegrationSliceRef.current = chartsIntegrationSlice;
  const lastEmittedViewStateRef = useRef<WorkflowViewState | null>(null);
  const activeChartId = chartsIntegrationSlice.activeChartId ?? WORKFLOW_LIST_CHART_ID;
  const [chartsPanelSpotlit, setChartsPanelSpotlit] = useState(false);
  const spotlightTimeoutRef = useRef<number | undefined>(undefined);
  const [drilldownBuckets, setDrilldownBuckets] = useState<WorkflowDrilldownBucket[]>([]);
  const [drilldownArrival, acknowledgeDrilldownArrival] = useConsumePendingParam(
    params.workflowDrilldown
  );

  const lastPushedChartsSliceRef = useRef<WorkflowListChartsIntegrationSlice | null>(null);
  const initialFilterModel = useMemo(() => {
    if (getIsGridFilterModel(appliedInitialState?.filterModel)) {
      return appliedInitialState.filterModel;
    }
    return {
      items: [],
      logicOperator: GridLogicOperator.And,
      quickFilterValues: [],
    };
  }, [appliedInitialState?.filterModel]);
  const initialSortModel = useMemo<GridSortModel>(
    () =>
      appliedInitialState?.sortModel.flatMap(item => {
        if (item.sort !== 'asc' && item.sort !== 'desc') return [];
        return [{ field: item.field, sort: item.sort }];
      }) ?? [{ field: 'createdAt', sort: 'desc' }],
    [appliedInitialState?.sortModel]
  );
  const activeChart = chartsIntegrationSlice.charts?.[activeChartId];
  const activeChartGrain = getResolvedWorkflowChartGrain({
    completed: appliedInitialState?.workflowFilter?.completed,
    dimensions: getWorkflowListChartDimensions(activeChart?.dimensions),
    filterModel: initialFilterModel,
    grain: activeChart?.grain,
    measure: getWorkflowListChartMeasure(activeChart?.values),
  });
  const rowNoun =
    activeChartGrain === WORKFLOW_CHART_GRAINS.columnInterval ? 'status intervals' : 'workflows';
  const {
    dataSource,
    dataSourceCache,
    filterModelTuple: [filterModel, setFilterModel],
    isGrouped,
    onDataSourceError,
    sortModelTuple: [sortModel, setSortModel],
    totalCount,
  } = useWorkflowDataSource(workflowFilter, {
    apiRef,
    grain: activeChartGrain,
    initialFilterModel,
    initialSortModel,
    rowGroupingModel,
  });
  const viewUser = useMemo((): WorkflowListPresetUser | null => {
    if (!currentUser) return null;

    return {
      __typename: 'User',
      displayName: currentUser.displayName,
      email: currentUser.email,
      id: currentUser.id,
    };
  }, [currentUser]);

  // Non-personal views ignore currentUser; keep this null so user load doesn't reset filters.
  const filterModelUser =
    workflowListPreset === 'requested-by-me' || workflowListPreset === 'assigned-to-me'
      ? viewUser
      : null;

  const syncChartStateFromGrid = useCallback(() => {
    if (variant === 'chart-builder' && appliedBuilderStateRef.current !== initialViewState) return;
    const gridApi = apiRef.current;
    if (!gridApi) return;

    const chartsState = gridApi.state.chartsIntegration;
    const previous = chartsIntegrationSliceRef.current;
    const controlledChartIds =
      variant === 'chart-builder' ? Object.keys(previous.charts ?? {}) : undefined;
    const liveChartIds = getWorkflowListChartIdsForSync({
      controlledChartIds,
      gridChartIds: Object.keys(chartsState.charts),
      ignoredChartIds: gridOnlyChartIdsRef.current,
    });
    const liveChartIdSet = new Set(liveChartIds);
    const gridActiveChartId = chartsState.activeChartId || WORKFLOW_LIST_CHART_ID;
    // A deleted or grid-only Chart can still be the grid's active one; move off it.
    const activeChartId = !liveChartIdSet.has(gridActiveChartId)
      ? (liveChartIds.at(0) ?? WORKFLOW_LIST_CHART_ID)
      : gridActiveChartId;
    if (activeChartId !== gridActiveChartId) {
      gridApi.setActiveChartId(activeChartId);
    }
    const activeChart = chartsState.charts[activeChartId];
    const dimensionItems = getWorkflowListChartDimensionItems(
      activeChart?.dimensions,
      previous.charts?.[activeChartId]?.dimensions
    );
    if (!isEqual(activeChart?.dimensions, dimensionItems))
      gridApi.updateChartDimensionsData(activeChartId, dimensionItems);

    const dimensions = getWorkflowListChartDimensions(dimensionItems);
    const values = getWorkflowListSingleChartValue(
      activeChart?.values,
      previous.charts?.[activeChartId]?.values
    );
    if (!isEqual(activeChart?.values, values)) {
      gridApi.updateChartValuesData(activeChartId, values);
    }
    const measure = getWorkflowListChartMeasure(values);
    /**
     * Only the builder's grid is a preview of the Chart's cohort, so only there does
     * grouping follow the Chart's Dimensions. The list has no Chart fields to follow
     * and owns its grouping through the Group menu and saved Views, which this would
     * otherwise reset on the next grid state change.
     */
    if (variant === 'chart-builder') {
      const nextGrouping = getWorkflowListGroupFieldsFromChartDimensions(dimensions);
      setRowGroupingModel(previous => (isEqual(previous, nextGrouping) ? previous : nextGrouping));
    }
    const nextAggregationModel = { [measure]: measure };
    const currentAggregationModel = gridApi.state.aggregation?.model ?? {};
    if (!isEqual(currentAggregationModel, nextAggregationModel)) {
      gridApi.setAggregationModel(nextAggregationModel);
    }

    const nextCharts: NonNullable<WorkflowListChartsIntegrationSlice['charts']> = {};
    for (const chartId of liveChartIds) {
      const chart = chartsState.charts[chartId];
      if (!chart) {
        const controlledChart = previous.charts?.[chartId];
        if (controlledChart) nextCharts[chartId] = controlledChart;
        continue;
      }
      /**
       * Grid state holds only Dimensions and Values; the Chart type lives in the
       * charts-integration context, so it comes from the slice the proxies maintain.
       */
      nextCharts[chartId] = {
        chartType: previous.charts?.[chartId]?.chartType,
        dateBucket: previous.charts?.[chartId]?.dateBucket,
        dimensions:
          chartId === activeChartId
            ? dimensionItems
            : getWorkflowListChartDimensionItems(
                chart.dimensions,
                previous.charts?.[chartId]?.dimensions
              ),
        grain: previous.charts?.[chartId]?.grain,
        insightValues: previous.charts?.[chartId]?.insightValues,
        minSampleSize: previous.charts?.[chartId]?.minSampleSize,
        title: previous.charts?.[chartId]?.title,
        values:
          chartId === activeChartId
            ? values
            : getWorkflowListSingleChartValue(chart.values, previous.charts?.[chartId]?.values),
      };
    }
    const nextSlice = { activeChartId, charts: nextCharts };
    if (!isEqual(previous, nextSlice)) {
      onChartsIntegrationSliceChange(nextSlice);
    }
  }, [apiRef, initialViewState, onChartsIntegrationSliceChange, variant]);

  /**
   * The panel lives at the far side of a tall grid, so opening it is not enough on
   * its own: bring it into view and outline it, or the user is left looking at the
   * Chart card wondering what the button did. Entry points that already sit inside
   * the panel opt out of the scroll.
   */
  const revealChartsPanel = useCallback(
    (tab: WorkflowListChartsTab, { scrollIntoView = true }: { scrollIntoView?: boolean } = {}) => {
      if (readOnly) return;
      setChartsPanelTab(tab);
      apiRef.current?.setChartsPanelOpen(true);
      setChartsPanelSpotlit(true);
      if (scrollIntoView) {
        /**
         * The panel is taller than the viewport, so `'nearest'` would consider it
         * already in view and stay put. Align its top instead, after the frame that
         * mounts it.
         */
        window.requestAnimationFrame(() => {
          listContainerRef.current
            ?.querySelector(`.${gridClasses.sidebar}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
      window.clearTimeout(spotlightTimeoutRef.current);
      spotlightTimeoutRef.current = window.setTimeout(
        () => setChartsPanelSpotlit(false),
        WORKFLOW_LIST_CHARTS_PANEL_SPOTLIGHT_MS
      );
    },
    [apiRef, readOnly]
  );

  /** Sends the panel to one Chart, on the tab the entry point asked for. */
  const focusChartInPanel = useCallback(
    (chartId: string, tab: WorkflowListChartsTab) => {
      if (readOnly) return;
      apiRef.current?.setActiveChartId(chartId);
      revealChartsPanel(tab);
    },
    [apiRef, readOnly, revealChartsPanel]
  );

  /** The Charts panel owns the type picker; mirror its choice into the saved View slice. */
  const handleChartTypeChange = useCallback(
    (chartId: string, chartType: WorkflowChartType) => {
      const previous = chartsIntegrationSliceRef.current;
      const chart = previous.charts?.[chartId];
      if (!chart || chart.chartType === chartType) return;
      const nextSlice = {
        ...previous,
        charts: { ...previous.charts, [chartId]: { ...chart, chartType } },
      };
      chartsIntegrationSliceRef.current = nextSlice;
      onChartsIntegrationSliceChange(nextSlice);
    },
    [onChartsIntegrationSliceChange]
  );

  const applyWorkflowViewStateToList = useCallback(
    (state: unknown, buckets: WorkflowDrilldownBucket[]) => {
      const applied = getAppliedWorkflowListStateFromWorkflowView(state);
      const baseFilterModel = getIsGridFilterModel(applied.filterModel)
        ? applied.filterModel
        : { items: [] };
      const nextFilterModel = composeFilterModelWithDrilldown({
        buckets,
        filterModel: baseFilterModel,
      });
      setFilterModel(previous => (isEqual(previous, nextFilterModel) ? previous : nextFilterModel));
      const nextSortModel = applied.sortModel.flatMap(item => {
        if (item.sort !== 'asc' && item.sort !== 'desc') return [];
        return [{ field: item.field, sort: item.sort }];
      });
      setSortModel(previous => (isEqual(previous, nextSortModel) ? previous : nextSortModel));
      setRowGroupingModel(previous =>
        isEqual(previous, applied.rowGroupingModel) ? previous : applied.rowGroupingModel
      );
      setDrilldownBuckets(previous => (isEqual(previous, buckets) ? previous : buckets));
      const gridApi = apiRef.current;
      if (gridApi) {
        const viewChartIds = new Set(Object.keys(applied.chartsIntegration.charts ?? {}));
        gridOnlyChartIdsRef.current = Object.keys(gridApi.state.chartsIntegration.charts).filter(
          chartId => !viewChartIds.has(chartId)
        );
        const nextAggregationModel = applied.aggregationModel;
        const currentAggregationModel = gridApi.state.aggregation?.model ?? {};
        if (!isEqual(currentAggregationModel, nextAggregationModel))
          gridApi.setAggregationModel(nextAggregationModel);
        lastPushedChartsSliceRef.current = pushChartsIntegrationIfChanged({
          apply: slice => applyChartsIntegrationToGrid(gridApi, slice),
          lastPushed: lastPushedChartsSliceRef.current,
          next: applied.chartsIntegration,
        });
      }
      // After grid sync so dateBucket / multi-chart slice wins over grid-derived state.
      const previousSlice = chartsIntegrationSliceRef.current;
      chartsIntegrationSliceRef.current = applied.chartsIntegration;
      if (!isEqual(previousSlice, applied.chartsIntegration))
        onChartsIntegrationSliceChange(applied.chartsIntegration);
    },
    [apiRef, onChartsIntegrationSliceChange, setFilterModel, setSortModel]
  );

  const clearDrilldownOverlay = useCallback(() => {
    setFilterModel(previous => ({
      ...previous,
      items: omitDrilldownFilterItems(previous.items ?? []),
    }));
    setDrilldownBuckets([]);
  }, [setFilterModel]);

  /**
   * MUI string/date columns default to `chartable: true`, which puts filter-only
   * fields like Workflow and Requester in the Charts panel with a + that cannot
   * add them. Only Dimensions and Measures belong there. `groupable` follows
   * `WORKFLOW_CHART_DIMENSION_GROUPABLE`, and groupable columns that supply a
   * `renderCell` are wrapped so the Group-by column shows the bucket label.
   */
  const columnDefs = useMemo(
    () =>
      [
        {
          field: 'name',
          filterOperators: getStringOperatorsWithSafeIsAnyOf(),
          filterable: true,
          flex: 1,
          headerName: 'Workflow',
          minWidth: 240,
          renderCell: WorkflowListTitleCell,
          type: 'string' as const,
        },
        {
          field: WORKFLOW_CHART_DIMENSIONS.workflowType,
          filterOperators: allWorkflowTypeFilters,
          filterable: true,
          flex: 0,
          headerName: 'Workflow type',
          minWidth: 160,
          renderCell: WorkflowListWorkflowTypeCell,
          sortable: false,
          valueGetter: (_value: unknown, row: WorkflowListGridRow) =>
            getIsWorkflowListWorkflowRow(row) ? (row.sourceWorkflow?.id ?? null) : null,
        },
        {
          field: 'board',
          filterOperators: allBoardFilters,
          filterable: true,
          flex: 0,
          headerName: 'Board',
          minWidth: 160,
          renderCell: WorkflowListBoardCell,
          sortable: false,
        },
        {
          field: 'column',
          filterOperators: allColumnFilters,
          filterable: true,
          flex: 0,
          headerName: 'Status',
          minWidth: 160,
          renderCell: WorkflowListColumnCell,
          sortable: false,
        },
        {
          // Hidden column for status-type filtering
          field: 'column-category',
          filterOperators: allColumnCategoryFilters,
          filterable: true,
          headerName: 'Status Type',
          sortable: false,
          valueGetter: (_value: unknown, row: WorkflowListGridRow) =>
            getIsWorkflowListWorkflowRow(row) ? row.column?.category : null,
        },
        {
          field: 'owner',
          filterOperators: allOwnerFilters,
          filterable: true,
          flex: 0,
          headerName: 'Requester',
          minWidth: 140,
          renderCell: (params: { field: string; row: WorkflowListGridRow }) => {
            if (!getIsWorkflowListWorkflowRow(params.row)) return null;
            return <OwnerCell showName row={params.row} />;
          },
          sortable: false,
        },
        {
          field: 'labels',
          filterOperators: allLabelFilters,
          filterable: true,
          flex: 0,
          headerName: 'Labels',
          minWidth: 220,
          renderCell: WorkflowListLabelCell,
          sortable: false,
        },
        {
          field: WORKFLOW_CHART_DIMENSIONS.assignee,
          filterOperators: allWorkflowAssigneeFilters,
          filterable: true,
          flex: 0,
          headerName: 'Assignees',
          minWidth: 120,
          renderCell: WorkflowAssigneesCell,
          sortable: false,
        },
        {
          field: WORKFLOW_CHART_DIMENSIONS.priority,
          filterOperators: allWorkflowPriorityFilters,
          filterable: true,
          headerName: 'Priority',
          sortable: false,
        },
        {
          // Hidden column for signing-status filtering
          field: 'state',
          filterOperators: allWorkflowStateFilters,
          filterable: true,
          headerName: 'Signing Status',
          sortable: false,
        },
        {
          // Hidden column for started-at filtering in the advanced Filters panel
          field: 'startedAt',
          filterOperators: workflowDateFilterOperators,
          filterable: true,
          headerName: 'Started',
          type: 'date' as const,
          valueGetter: (_value: unknown, row: WorkflowListGridRow) =>
            getIsWorkflowListWorkflowRow(row) ? dateValueGetter(row.startedAt) : null,
        },
        {
          field: WORKFLOW_CHART_DIMENSIONS.createdAt,
          filterOperators: workflowDateFilterOperators,
          filterable: true,
          flex: 0,
          headerName: 'Created',
          minWidth: 120,
          renderCell: WorkflowListDateCell,
          type: 'date' as const,
          valueGetter: (_value: unknown, row: WorkflowListGridRow) =>
            getIsWorkflowListWorkflowRow(row) ? dateValueGetter(row.createdAt) : null,
        },
        {
          field: WORKFLOW_CHART_DIMENSIONS.completedAt,
          filterOperators: workflowDateFilterOperators,
          filterable: true,
          flex: 0,
          headerName: 'Completed',
          minWidth: 120,
          renderCell: WorkflowListDateCell,
          type: 'date' as const,
          valueGetter: (_value: unknown, row: WorkflowListGridRow) =>
            getIsWorkflowListWorkflowRow(row) ? dateValueGetter(row.completedAt) : null,
        },
        {
          field: WORKFLOW_CHART_DIMENSIONS.signedAt,
          filterOperators: workflowDateFilterOperators,
          filterable: true,
          flex: 0,
          headerName: 'Signed at',
          minWidth: 120,
          renderCell: WorkflowListDateCell,
          type: 'date' as const,
          valueGetter: (_value: unknown, row: WorkflowListGridRow) => {
            if (!getIsWorkflowListWorkflowRow(row)) return null;
            const signingPhase = row.phases.find(phase => phase.__typename === 'SigningPhase');
            return dateValueGetter(signingPhase?.completedAt);
          },
        },
        {
          aggregable: true,
          field: WORKFLOW_CHART_MEASURES.count,
          filterable: false,
          headerName: WORKFLOW_LIST_MEASURE_COLUMN_NAMES[WORKFLOW_CHART_MEASURES.count],
          sortable: false,
          type: 'number' as const,
          valueGetter: () => 1,
        },
        {
          aggregable: true,
          field: WORKFLOW_CHART_MEASURES.medianDurationDays,
          filterable: false,
          headerName:
            WORKFLOW_LIST_MEASURE_COLUMN_NAMES[WORKFLOW_CHART_MEASURES.medianDurationDays],
          sortable: false,
          type: 'number' as const,
          valueGetter: (_value: unknown, row: WorkflowListGridRow) => {
            if (getIsWorkflowListGroupRow(row))
              return row.medianDurationDays ?? row.avgCycleTime ?? null;
            if (!getIsWorkflowListWorkflowRow(row)) return null;
            return getWorkflowCycleTimeDays(row);
          },
        },
      ].map(column => ({
        ...column,
        chartable: getIsWorkflowListChartableField(column.field),
        groupable: getIsWorkflowListGroupableDimension(column.field),
        renderCell: getWorkflowListGroupAwareRenderCell(column),
      })),
    []
  );
  const gridColumnDefs = useMemo(
    () =>
      variant === 'chart-builder'
        ? columnDefs.filter(column => column.field !== 'labels')
        : columnDefs,
    [columnDefs, variant]
  );

  /**
   * The grid echoes the controlled model back through this handler, so a fresh
   * array here would feed it a new identity on every render. The data source
   * reads that as a grouping change, drops the tree it just built and refetches,
   * which leaves the list permanently empty.
   */
  const handleRowGroupingModelChange = useCallback((model: GridRowGroupingModel) => {
    const allowlisted = model
      .filter(getIsWorkflowListGroupableDimension)
      .slice(0, MAX_WORKFLOW_CHART_DIMENSIONS);
    setRowGroupingModel(previous => (isEqual(previous, allowlisted) ? previous : allowlisted));
  }, []);

  const controlledGridProps = {
    dataSource,
    dataSourceCache,
    filterModel,
    rowGroupingModel,
    sortModel,
  };
  const activeFilterCount =
    filterModel.items.length + (filterModel.quickFilterValues?.length ? 1 : 0);
  const chartTitles = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(chartsIntegrationSlice.charts ?? {}).map(([chartId, chart]) => [
          chartId,
          chart.title?.trim() || UNTITLED_WORKFLOW_LIST_CHART_TITLE,
        ])
      ),
    [chartsIntegrationSlice.charts]
  );
  const builderPanelContext = useMemo(
    () => ({
      chartTitles,
      onAddChart: onAddChart ?? (() => undefined),
      readOnly,
    }),
    [chartTitles, onAddChart, readOnly]
  );
  const chartPreviewEntries = Object.entries(chartsIntegrationSlice.charts ?? {});
  /**
   * The preview is a two-column grid, so an odd Chart count would leave a ragged
   * gap. Widening the lead Chart fills it and reads as the View's headline.
   */
  const leadChartIsWide = chartPreviewEntries.length % 2 === 1;
  const groupingHeaderName = rowGroupingModel.length
    ? `Group by (${rowGroupingModel
        .map(
          field =>
            gridColumnDefs.find(column => column.field === field)?.headerName ?? capitalCase(field)
        )
        .join(', ')})`
    : undefined;

  /**
   * The toolbar sits above this route and reaches the grid through this shared
   * ref, which has to be cleared on unmount because the grid nulls `current`
   * and the toolbar would read a dead api. Cleanup also runs on every tab hide
   * (React.Activity), so this has to be a layout effect: re-registering before
   * the next paint keeps the toolbar from painting one frame with its filter
   * chips missing and then popping them back in.
   */
  useLayoutEffect(() => {
    onApiRefChange(apiRef);
    return () => onApiRefChange(null);
  }, [apiRef, onApiRefChange]);

  /**
   * A new Chart arrives without a type, so the editor asks for the type picker on
   * its behalf. Only a new request counts: setup re-runs on every tab re-show.
   * Add-a-chart is a control inside the panel, so the panel is already under the
   * user's eye — scrolling to it would only yank the page away from the title
   * input the new card just focused.
   */
  useEffect(() => {
    if (!chartFieldsRequestKey || chartFieldsRequestKey === handledChartFieldsRequestRef.current) {
      return;
    }
    handledChartFieldsRequestRef.current = chartFieldsRequestKey;
    revealChartsPanel(WORKFLOW_LIST_CHARTS_TABS.chart, { scrollIntoView: false });
  }, [chartFieldsRequestKey, revealChartsPanel]);

  useEffect(() => {
    const gridApi = apiRef.current;
    if (!gridApi) return;
    syncChartStateFromGrid();
    return gridApi.subscribeEvent('stateChange', syncChartStateFromGrid);
  }, [apiRef, syncChartStateFromGrid]);

  useEffect(() => {
    if (!drilldownArrival) return;
    const payload = parseWorkflowDrilldownPayload(drilldownArrival.value);
    acknowledgeDrilldownArrival();
    if (!payload) return;

    if (payload.viewState != null) {
      applyWorkflowViewStateToList(payload.viewState, payload.buckets);
      return;
    }

    if (payload.presetId) {
      if (!getIsWorkflowSystemChartPresetId(payload.presetId)) {
        setDrilldownBuckets([]);
        return;
      }
      const preset = getWorkflowSystemChartPreset(payload.presetId);
      const { state } = getWorkflowViewStateFromSystemChartPreset(preset);
      applyWorkflowViewStateToList(state, payload.buckets);
    }
  }, [acknowledgeDrilldownArrival, applyWorkflowViewStateToList, drilldownArrival]);

  /** Apply a focused builder View once, after the grid APIs are available. */
  useEffect(() => {
    if (variant !== 'chart-builder' || !initialViewState) return;
    if (appliedBuilderStateRef.current === initialViewState) return;
    appliedBuilderStateRef.current = initialViewState;
    applyWorkflowViewStateToList(initialViewState, []);
    apiRef.current?.setChartsPanelOpen(!readOnly);
  }, [apiRef, applyWorkflowViewStateToList, initialViewState, readOnly, variant]);

  /**
   * Add controlled Charts to MUI after the owner commits them to the draft.
   * Tab re-shows re-run this setup with the same slice; skip if we already wrote it.
   */
  useEffect(() => {
    if (variant !== 'chart-builder' || appliedBuilderStateRef.current !== initialViewState) return;
    const gridApi = apiRef.current;
    if (!gridApi) return;
    lastPushedChartsSliceRef.current = pushChartsIntegrationIfChanged({
      apply: slice => applyChartsIntegrationToGrid(gridApi, slice),
      lastPushed: lastPushedChartsSliceRef.current,
      next: chartsIntegrationSlice,
    });
  }, [apiRef, chartsIntegrationSlice, initialViewState, variant]);

  useEffect(() => {
    if (!onViewStateChange || variant !== 'chart-builder') return;
    const gridApi = apiRef.current;
    if (!gridApi) return;
    const nextState = getWorkflowViewStateFromWorkflowList({
      aggregationModel: gridApi.state.aggregation?.model,
      chartsIntegration: chartsIntegrationSlice,
      filterModel,
      rowGroupingModel,
      sortModel,
      workflowFilter: appliedInitialState?.workflowFilter,
    });
    if (isEqual(lastEmittedViewStateRef.current, nextState)) return;
    lastEmittedViewStateRef.current = nextState;
    onViewStateChange(nextState);
  }, [
    apiRef,
    appliedInitialState?.workflowFilter,
    chartsIntegrationSlice,
    filterModel,
    onViewStateChange,
    rowGroupingModel,
    sortModel,
    variant,
  ]);

  /**
   * Selecting a view replaces the grid's filter items, which clears the data
   * source cache and scrolls back to the top. Effect setup re-runs every time
   * the tab is re-shown (React.Activity), so only apply the view when it has
   * actually changed, and keep the search box's quick filter either way.
   */
  useEffect(() => {
    if (variant === 'chart-builder') return;
    const viewKey = `${activeWorkflowViewId ?? 'system'}:${workflowListPreset}:${filterModelUser?.id ?? ''}`;
    if (appliedViewKeyRef.current === viewKey) return;
    appliedViewKeyRef.current = viewKey;
    if (activeWorkflowViewId) return;

    const viewFilterModel = getWorkflowListPresetFilterModel({
      currentUser: filterModelUser,
      presetId: workflowListPreset,
    });
    setFilterModel(previous => ({
      ...viewFilterModel,
      quickFilterValues: previous.quickFilterValues,
    }));
  }, [activeWorkflowViewId, filterModelUser, setFilterModel, variant, workflowListPreset]);

  /**
   * Same as the Documents grid: the grouping column already renders the title
   * via `leafField: 'name'`, so keep the Workflow column out of the way.
   */
  useEffect(() => {
    if (!apiRef.current) return;
    const isNameVisible = gridColumnVisibilityModelSelector(apiRef).name !== false;
    if (!getShouldSetWorkflowListNameColumnVisibility({ isGrouped, isNameVisible })) return;
    apiRef.current.setColumnVisibility('name', !isGrouped);
  }, [apiRef, isGrouped]);

  return (
    <div ref={listContainerRef} className={classes.listContainer}>
      {Boolean(drilldownBuckets.length) && (
        <Alert
          action={
            <Button color="inherit" size="small" onClick={clearDrilldownOverlay}>
              Clear
            </Button>
          }
          className={classes.drilldownBanner}
          severity="info"
        >
          Drill-down: {drilldownBuckets.map(bucket => bucket.label).join(' · ')}
        </Alert>
      )}
      {variant === 'chart-builder' && (
        <div className={classes.chartPreview}>
          {chartPreviewEntries.map(([chartId, chart], index) => {
            const dimensions = getWorkflowListChartDimensions(chart.dimensions);
            const seriesInput = getWorkflowListSeriesInput({
              dateBucket: getWorkflowListChartDateBucket(chart, dimensions),
              dimensions,
              filterModel,
              grain: chart.grain,
              insightValues: chart.insightValues,
              measure: getWorkflowListChartMeasure(chart.values),
              minSampleSize: chart.minSampleSize,
              workflowFilter,
            });
            const titleEditor = onActiveChartTitleChange
              ? {
                  disabled: readOnly,
                  initiallyEditing: chartTitleEditingKey === chartId,
                  onSave: (title: string) => onActiveChartTitleChange(chartId, title),
                }
              : undefined;
            const previewActions = (
              <>
                {renderChartLeadingActions?.(chartId)}
                {!readOnly && (
                  <AlineTooltip arrow placement="top" title="Configure chart">
                    <IconButton
                      aria-label="Configure chart"
                      size="small"
                      onClick={() => focusChartInPanel(chartId, WORKFLOW_LIST_CHARTS_TABS.chart)}
                    >
                      <SlidersHorizontal size={16} />
                    </IconButton>
                  </AlineTooltip>
                )}
                {renderChartActions?.(chartId)}
              </>
            );

            return (
              <InsightChartCard
                key={chartId}
                chartType={getWorkflowListChartType(chart)}
                emptyState={
                  <WorkflowListChartFieldsPrompt
                    readOnly={readOnly}
                    onChoose={() => focusChartInPanel(chartId, WORKFLOW_LIST_CHARTS_TABS.fields)}
                  />
                }
                headerActions={previewActions}
                seriesInput={seriesInput}
                title={chartTitles[chartId] ?? UNTITLED_WORKFLOW_LIST_CHART_TITLE}
                titleEditor={titleEditor}
                wide={index === 0 && leadChartIsWide}
              />
            );
          })}
        </div>
      )}
      {variant === 'chart-builder' && (
        <div className={classes.workflowsToolbar}>
          <div className={classes.toolbarHeading}>
            <Typography noWrap variant="subtitle1">
              {chartTitles[activeChartId] ?? UNTITLED_WORKFLOW_LIST_CHART_TITLE}
            </Typography>
            <Typography color="textSecondary" variant="body2">
              · {totalCount.toLocaleString()} {rowNoun}
            </Typography>
          </div>
          <Button
            startIcon={<ListFilter size={16} />}
            onClick={() => apiRef.current?.showPreferences(GridPreferencePanelsValue.filters)}
          >
            Filters{activeFilterCount ? ` · ${activeFilterCount}` : ''}
          </Button>
        </div>
      )}
      <WorkflowListBuilderPanelContext.Provider value={builderPanelContext}>
        <GridChartsIntegrationContextProvider>
          <WorkflowListChartProxies
            charts={chartsIntegrationSlice.charts ?? {}}
            onChartTypeChange={handleChartTypeChange}
          />
          <DataGridPremium<WorkflowListGridRow>
            chartsIntegration
            disableColumnMenu
            disableColumnResize
            disableRowSelectionOnClick
            lazyLoading
            pagination
            {...controlledGridProps}
            aggregationFunctions={WORKFLOW_LIST_AGGREGATION_FUNCTIONS}
            apiRef={apiRef}
            checkboxSelection={variant === 'list'}
            className={cx(classes.xGrid, chartsPanelSpotlit && classes.chartsPanelSpotlight)}
            columns={gridColumnDefs}
            getAggregationPosition={getWorkflowListAggregationPosition}
            getEstimatedRowHeight={() => reportRowHeightPx}
            getRowHeight={() => 'auto'}
            groupingColDef={
              isGrouped
                ? {
                    headerName: groupingHeaderName,
                    hideDescendantCount: true,
                    leafField: 'name',
                    // MUI sizes this column from the grouped-by column, which is too
                    // narrow for both the "Group by (…)" header and a bucket label.
                    minWidth: 260,
                    valueGetter: (_value, row) => getGroupingColumnLabel(row),
                  }
                : undefined
            }
            initialState={{
              aggregation: {
                model: { [WORKFLOW_CHART_MEASURES.count]: WORKFLOW_CHART_MEASURES.count },
              },
              chartsIntegration: {
                activeChartId: WORKFLOW_LIST_CHART_ID,
                charts: { [WORKFLOW_LIST_CHART_ID]: createDefaultWorkflowListChartConfig() },
              },
              columns: {
                columnVisibilityModel: {
                  /**
                   * Date, priority, signing status, and status-type columns remain
                   * available for filtering and customization but start hidden.
                   * Measure columns exist for Charts + aggregation.
                   */
                  [WORKFLOW_CHART_MEASURES.medianDurationDays]: false,
                  [WORKFLOW_CHART_MEASURES.count]: false,
                  [WORKFLOW_CHART_DIMENSIONS.completedAt]: false,
                  [WORKFLOW_CHART_DIMENSIONS.createdAt]: false,
                  [WORKFLOW_CHART_DIMENSIONS.signedAt]: false,
                  'column-category': false,
                  name: !isGrouped,
                  priority: false,
                  startedAt: false,
                  state: false,
                },
              },
              pagination: {
                paginationModel: { page: 0, pageSize: 50 },
              },
              sorting: {
                sortModel: [{ field: 'createdAt', sort: 'desc' }],
              },
            }}
            slotProps={{
              baseTabs: {
                onChange: (_event, value) => setChartsPanelTab(value),
                value: chartsPanelTab,
              },
              chartsPanel: {
                getColumnName: field =>
                  getIsWorkflowChartMeasure(field)
                    ? WORKFLOW_LIST_MEASURE_COLUMN_NAMES[field]
                    : undefined,
                schema: WORKFLOW_LIST_CHARTS_PANEL_SCHEMA,
              },
              filterPanel: {
                filterableColumns: 'all',
              },
              footer: {
                noun: rowNoun,
                totalCount,
              },
              panel: {
                placement: 'bottom-start',
                target: filtersAnchorEl ?? undefined,
              },
            }}
            slots={{
              baseMenuList: WorkflowListChartsPanelMenuList,
              baseTabs: WorkflowListChartsTabs,
              baseTooltip: GridAlineTooltip,
              chartsPanel:
                variant === 'chart-builder'
                  ? WorkflowListBuilderChartsPanel
                  : WorkflowListChartsPanel,
              filterPanel: ReportFiltersPanel,
              footer: WorkflowListFooter,
              noRowsOverlay: WorkflowsListEmpty,
            }}
            sx={createDataGridPremiumPaperStyles}
            onDataSourceError={onDataSourceError}
            onFilterModelChange={setFilterModel}
            onRowClick={({ row }, event) => {
              if (!getIsWorkflowListWorkflowRow(row)) return;
              /**
               * A modifier or middle click is a request for a new browser tab. The row background is
               * not a link, so honouring it here would navigate the current tab instead — do nothing
               * and let the row's name link be the thing that opens in a new tab.
               */
              if (getIsBrowserHandledLinkClick(event)) return;

              openLink(
                link.WorkflowDetailTabSummary({ workflowId: getWorkflowIdFromListRow(row) }),
                {
                  title: row.name || 'Untitled Workflow',
                }
              );
            }}
            onRowGroupingModelChange={handleRowGroupingModelChange}
            onSortModelChange={setSortModel}
          />
        </GridChartsIntegrationContextProvider>
      </WorkflowListBuilderPanelContext.Provider>
    </div>
  );
}

/**
 * The built-in Charts panel header only offers a chart selector once a second Chart
 * exists and has no way to create one, so it is replaced with a header that always
 * shows the selector and can add Charts. `GridChartsPanel` has no header slot; it
 * renders its header as the element right after ours, which the stylesheet hides.
 */
function WorkflowListChartsPanel(props: GridChartsPanelProps): JSX.Element {
  const { classes } = useChartsPanelStyles();
  const apiRef = useGridApiContext();
  const { chartStateLookup } = useGridChartsIntegrationContext();
  const activeChartId = useGridSelector(
    apiRef,
    api => api.current.state.chartsIntegration.activeChartId
  );
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const chartIds = Object.keys(chartStateLookup);

  const closeMenu = useCallback(() => setMenuAnchorEl(null), []);

  const handleAddChart = useCallback(() => {
    setMenuAnchorEl(null);
    addWorkflowListChart(apiRef.current);
  }, [apiRef]);

  const handleClosePanel = useCallback(() => {
    apiRef.current.setChartsPanelOpen(false);
  }, [apiRef]);

  return (
    <>
      <div className={classes.chartsPanelHeader}>
        <Button
          className={classes.chartsPanelSelector}
          color="inherit"
          endIcon={<ChevronDown size={16} />}
          onClick={event => setMenuAnchorEl(event.currentTarget)}
        >
          {chartIds.length ? getWorkflowListChartLabel(activeChartId) : 'Charts'}
        </Button>
        <AlineTooltip arrow placement="top" title="Add a chart">
          <IconButton aria-label="Add a chart" size="small" onClick={handleAddChart}>
            <Plus size={16} />
          </IconButton>
        </AlineTooltip>
        <AlineTooltip arrow placement="top" title="Close charts panel">
          <IconButton aria-label="Close charts panel" size="small" onClick={handleClosePanel}>
            <X size={16} />
          </IconButton>
        </AlineTooltip>
      </div>
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu}>
        {chartIds.map(chartId => (
          <WorkflowListChartMenuItem
            key={chartId}
            chartId={chartId}
            selected={chartId === activeChartId}
            onSelect={closeMenu}
          />
        ))}
        {chartIds.length > 0 && <Divider />}
        <MenuItem onClick={handleAddChart}>
          <ListItemIcon>
            <Plus size={16} />
          </ListItemIcon>
          Add a chart
        </MenuItem>
      </Menu>
      <GridChartsPanel {...props} />
    </>
  );
}

function WorkflowListBuilderChartsPanel(props: GridChartsPanelProps): JSX.Element {
  const { classes } = useChartsPanelStyles();
  const apiRef = useGridApiContext();
  const context = React.useContext(WorkflowListBuilderPanelContext);
  const activeChartId = useGridSelector(
    apiRef,
    api => api.current.state.chartsIntegration.activeChartId
  );
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  if (!context) throw new Error('WorkflowListBuilderChartsPanel requires its builder context.');
  const chartIds = Object.keys(context.chartTitles);

  const handleSelectChart = (chartId: string) => {
    setMenuAnchorEl(null);
    apiRef.current.setActiveChartId(chartId);
  };

  return (
    <>
      <div className={classes.chartsPanelHeader}>
        <Button
          className={classes.chartsPanelSelector}
          color="inherit"
          disabled={!chartIds.length}
          endIcon={<ChevronDown size={16} />}
          onClick={event => setMenuAnchorEl(event.currentTarget)}
        >
          {context.chartTitles[activeChartId] ?? UNTITLED_WORKFLOW_LIST_CHART_TITLE}
        </Button>
        {!context.readOnly && (
          <AlineTooltip arrow placement="top" title="Add a chart">
            <IconButton aria-label="Add a chart" size="small" onClick={context.onAddChart}>
              <Plus size={16} />
            </IconButton>
          </AlineTooltip>
        )}
        <AlineTooltip arrow placement="top" title="Close charts panel">
          <IconButton
            aria-label="Close charts panel"
            size="small"
            onClick={() => apiRef.current.setChartsPanelOpen(false)}
          >
            <X size={16} />
          </IconButton>
        </AlineTooltip>
      </div>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        {chartIds.map(chartId => (
          <MenuItem
            key={chartId}
            selected={chartId === activeChartId}
            onClick={() => handleSelectChart(chartId)}
          >
            {context.chartTitles[chartId]}
          </MenuItem>
        ))}
      </Menu>
      <GridChartsPanel {...props} />
    </>
  );
}

/**
 * MUI offers Add to Measure for any unused field once Categories group the grid.
 * Hide section moves the server-series contract cannot render.
 */
function WorkflowListChartsPanelMenuList({
  children,
  ...props
}: GridSlotProps['baseMenuList']): JSX.Element {
  const apiRef = useGridApiContext();
  const field = getChartsPanelMenuField(props['aria-labelledby'], apiRef.current.getAllColumns());
  const addToDimensionsLabel = apiRef.current.getLocaleText('chartsMenuAddToDimensions')(
    WORKFLOW_LIST_CHARTS_DIMENSIONS_LABEL
  );
  const addToValuesLabel = apiRef.current.getLocaleText('chartsMenuAddToValues')(
    WORKFLOW_LIST_CHARTS_VALUES_LABEL
  );
  const items = collapseChartsPanelMenuDividers(
    React.Children.toArray(children).filter(child => {
      if (field == null || !React.isValidElement(child)) return true;
      return getIsWorkflowListChartsSectionMenuItemAllowed({
        addToDimensionsLabel,
        addToValuesLabel,
        field,
        menuItemLabel: getChartsPanelMenuItemLabel(getReactElementChildren(child)),
      });
    })
  );

  return <materialGridSlots.baseMenuList {...props}>{items}</materialGridSlots.baseMenuList>;
}

const CHARTS_PANEL_DATA_FIELD_CLASS = 'MuiDataGrid-chartsPanelDataField';

function getChartsPanelMenuField(
  labelledBy: GridSlotProps['baseMenuList']['aria-labelledby'],
  columns: ReadonlyArray<{ field: string; headerName?: string }>
): string | null {
  const triggerId = getFirstAriaLabelledById(labelledBy);
  if (!triggerId) return null;
  const fieldRoot = document
    .getElementById(triggerId)
    ?.closest(`.${CHARTS_PANEL_DATA_FIELD_CLASS}`);
  const label = fieldRoot?.textContent?.trim();
  if (!label) return null;
  return columns.find(column => (column.headerName ?? column.field) === label)?.field ?? null;
}

function getFirstAriaLabelledById(labelledBy: unknown): string | null {
  if (typeof labelledBy !== 'string' || !labelledBy) return null;
  return labelledBy.split(/\s+/)[0] ?? null;
}

function getReactElementChildren(element: React.ReactElement): unknown {
  const props = element.props;
  if (!getIsPropsRecord(props)) return undefined;
  return props.children;
}

function getIsPropsRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value != null;
}

function getChartsPanelMenuItemLabel(children: unknown): string {
  if (typeof children === 'string') return children;
  return '';
}

function getIsChartsPanelMenuDivider(node: React.ReactNode): boolean {
  if (!React.isValidElement(node)) return false;
  return String(node.key).includes('divider-');
}

function collapseChartsPanelMenuDividers(items: React.ReactNode[]): React.ReactNode[] {
  const collapsed: React.ReactNode[] = [];
  for (const item of items) {
    if (getIsChartsPanelMenuDivider(item) && !collapsed.length) continue;
    if (getIsChartsPanelMenuDivider(item) && getIsChartsPanelMenuDivider(collapsed.at(-1)))
      continue;
    collapsed.push(item);
  }
  if (collapsed.length && getIsChartsPanelMenuDivider(collapsed.at(-1))) collapsed.pop();
  return collapsed;
}

/** Renders the Charts panel tabs without the unsupported Customize tab. */
function WorkflowListChartsTabs({ items, ...props }: GridSlotProps['baseTabs']): JSX.Element {
  const supportedItems = useMemo(
    () => items.filter(item => item.value !== WORKFLOW_LIST_CHARTS_UNSUPPORTED_TAB),
    [items]
  );

  return <materialGridSlots.baseTabs {...props} items={supportedItems} />;
}

function WorkflowListChartMenuItem({
  chartId,
  onSelect,
  selected,
}: {
  chartId: string;
  onSelect: () => void;
  selected: boolean;
}): JSX.Element {
  const apiRef = useGridApiContext();

  const handleClick = useCallback(() => {
    apiRef.current.setActiveChartId(chartId);
    onSelect();
  }, [apiRef, chartId, onSelect]);

  return (
    <MenuItem selected={selected} onClick={handleClick}>
      {getWorkflowListChartLabel(chartId)}
    </MenuItem>
  );
}

/** Squares the footer's right corner while the Charts panel sits beside it. */
function WorkflowListFooter(props: ListCountFooterProps): JSX.Element {
  const apiRef = useGridApiContext();
  const sidePanelOpen = useGridSelector(apiRef, gridSidebarOpenSelector);
  return <ListCountFooter {...props} squareRight={sidePanelOpen} />;
}

/**
 * Registers every Chart on the view with the grid's charts-integration context: the
 * built-in Charts panel only lists registered charts, and grid APIs like
 * `setChartType` silently no-op for unregistered ids. The visible chart stays the
 * server-series InsightChartCard (ADR 0004 — numbers never come from loaded grid
 * rows), so the proxy renderer draws nothing.
 */
function WorkflowListChartProxies({
  charts,
  onChartTypeChange,
}: {
  charts: Record<string, WorkflowListChartConfig>;
  onChartTypeChange: (chartId: string, chartType: WorkflowChartType) => void;
}): JSX.Element {
  const { chartStateLookup, setChartState } = useGridChartsIntegrationContext();

  /**
   * The context is the only place the Chart type lives, so this effect keeps it and the
   * saved View slice in step, in both directions. Charts created after mount (Add Chart,
   * drill-down, applied Workflow Views) register only after `setChartType` already no-opped
   * on an unregistered id, leaving them with no type to render; those get the persisted
   * type backfilled. Once a chart has a type, the panel's picker is the source of truth.
   */
  useEffect(() => {
    for (const [chartId, chart] of Object.entries(charts)) {
      const desiredType = getWorkflowListChartType(chart);
      const action = getWorkflowListChartProxySyncAction({
        desiredType,
        registeredType: chartStateLookup[chartId]?.type,
      });
      if (action === WORKFLOW_LIST_CHART_PROXY_SYNC.skip) continue;
      if (action === WORKFLOW_LIST_CHART_PROXY_SYNC.backfill) {
        setChartState(chartId, { type: desiredType });
        continue;
      }
      const registeredType = chartStateLookup[chartId]?.type;
      if (registeredType && getIsWorkflowChartType(registeredType))
        onChartTypeChange(chartId, registeredType);
    }
  }, [charts, chartStateLookup, onChartTypeChange, setChartState]);

  return (
    <>
      {Object.keys(charts).map(chartId => (
        <GridChartsRendererProxy
          key={chartId}
          id={chartId}
          label={getWorkflowListChartLabel(chartId, charts[chartId])}
          renderer={WorkflowListNullChartRenderer}
        />
      ))}
    </>
  );
}

function WorkflowListNullChartRenderer(): null {
  return null;
}

function WorkflowsListEmpty(): JSX.Element {
  return (
    <ListEmpty
      icon={<WorkflowIcon fill="#9CA7AB" height={32} width={32} />}
      subtitle="Click new to create a workflow"
      title="No workflows"
    />
  );
}
